/**
 * Subscription Service
 * API client for subscription management, billing, and usage tracking
 */

import { apiClient, extractData } from './api';
import type { SubscriptionPlan, BillingInterval, MessageUsage } from '@kids-lms/types';

export interface SubscriptionStatusResponse {
  plan: SubscriptionPlan;
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  limits: {
    messagesPerDay: number;
    maxChildren: number;
  };
  usage: {
    children: number;
  };
}

export async function getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
  const response = await apiClient.get('/subscriptions/status');
  return extractData(response);
}

export async function createCheckout(
  plan: SubscriptionPlan,
  interval: BillingInterval = 'monthly'
): Promise<{ checkoutUrl: string }> {
  const referralCode = localStorage.getItem('mindora_ref') || undefined;
  const response = await apiClient.post('/subscriptions/checkout', { plan, interval, referralCode });
  return extractData(response);
}

export async function verifyCheckoutSession(sessionId: string): Promise<{
  plan: SubscriptionPlan;
  status: string;
  limits: { messagesPerDay: number; maxChildren: number };
}> {
  const response = await apiClient.post('/subscriptions/verify-session', { sessionId });
  return extractData(response);
}

export async function createPortalSession(): Promise<{ portalUrl: string }> {
  const response = await apiClient.post('/subscriptions/portal', {});
  return extractData(response);
}

export async function getMessageUsage(
  childId: string
): Promise<MessageUsage & { plan: SubscriptionPlan }> {
  const response = await apiClient.get(`/subscriptions/message-usage/${childId}`);
  return extractData(response);
}

export default {
  getSubscriptionStatus,
  createCheckout,
  verifyCheckoutSession,
  createPortalSession,
  getMessageUsage,
};
